/**
 * Copyright (c) 2019 CRF Box Oy. All Rights Reserved. Company Confidential.
 */

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { head, compose } from 'ramda';
import cloneDeep from 'lodash/cloneDeep';
import OrganizationDetailsCard from './organization.details.card';
import { Grid, Pagination } from 'tc-client-common';
import Notification from '../../common/notification/notification.component';
import { peopleDetails_peopleFlow, userManagementOrgStudy } from '../../../constants/localUrls';
import UserIcon from '../../../assets/images/icons/user-picture.svg';
import Loading from '../../loading/loading.component';
import PropTypes from 'prop-types';
import { MULTIPLE_STUDIES, MULTIPLE_ROLES, USERS } from '../../../const';
import { maybeMultiple, paginatePeople, SORT_PEOPLE_BY, sortPeople } from '../utils/people.utils';

const { usePagination, withPagination } = Pagination;
const PAGE_SIZE = 10;
const columnDefinition = [
  { label: 'Name', size: 25 },
  { label: 'Username', size: 25 },
  { label: 'Role', size: 25 },
  { label: 'Study', size: 25 }
];

const OrganizationDetails = ({ organizations, sponsor, fetchOrganizations, match, location, history }) => {
  const [loading, setLoading] = useState(false);
  const [peopleData, setPeopleData] = useState([]);
  const paginationApi = usePagination();
  useEffect(() => {
    const contentComponentWrapper = document.getElementById('content-component-wrapper');
    contentComponentWrapper && (contentComponentWrapper.scrollTop = 0);

    paginationApi.loading(true);
    setLoading(true);

    fetchOrganizations(sponsor.databasename)
      .then((data) => {
        //set the organizations in case the page is refreshed
        if (!organizations) {
          organizations = data;
        }
        paginationApi.loading(false);
        setLoading(false);
        selectPage({ nextPage: 1, pageSize: PAGE_SIZE });
      })
      .catch((error) => console.log(error));

    // used for breadcrumb in SSO redirect
    if (!location.state) {
      const { organizationId, organizationName } = match.params;

      history.push(location.pathname, {
        forBreadcrumb: { organizationId, organizationName }
      });
    }
  }, []);

  const selectPage = ({ nextPage, pageSize, onSuccess = () => {} }) => {
    paginationApi.show();
    const organizationData = (organizations &&
      organizations.find(({ id }) => String(id) === String(organizationId))) || { people: [] };
    if (organizationData.length === 0) {
      paginationApi.hide();
    }
    let users = organizationData.people;
    users = sortPeople('name')(users);
    const totalUsers = users.length;
    const pageCount = Math.ceil(totalUsers / pageSize);
    const paginatedUsers = paginatePeople(users, nextPage, pageSize);
    setPeopleData(paginatedUsers);
    paginationApi.update({ pageCount, pageSize, itemsCount: totalUsers, currentPage: nextPage });
  };

  paginationApi.selectPage(selectPage);

  const getRowsData = (id, name, people) => {
    const nameColumn = (row) => () => (
      <span className="people-column">
        <img className="people-icon" src={UserIcon} />
        <Link
          className="link"
          to={{
            pathname: peopleDetails_peopleFlow(row.userId, id, name),
            state: {
              user: row,
              forBreadcrumb: { ...row, organizationId: id, organizationName: name }
            }
          }}
        >{`${row.firstName} ${row.lastName}`}</Link>
      </span>
    );

    const roleColumn = (roles) => head(roles).roleNameDescription;

    const studyColumn = (row, organizationId, organizationName) => {
      const study = head(row.studies);

      return () => (
        <Link
          className="link"
          to={{
            pathname: userManagementOrgStudy(organizationId, organizationName, study.gid),
            state: {
              forBreadcrumb: { ...row, studyGid: study.gid, studyName: study.name, organizationId, organizationName }
            }
          }}
        >
          {study.name}
        </Link>
      );
    };

    const getSingleRow = (row) => {
      const assignmentId = row.assignmentId || '';
      const username = row.username || '';
      const role = maybeMultiple({
        multiple: () => MULTIPLE_ROLES,
        single: () => roleColumn(row.roles)
      })(row.roles);
      const study = maybeMultiple({
        multiple: () => MULTIPLE_STUDIES,
        single: () => studyColumn(row, id, name)
      })(row.studies);

      return {
        id: assignmentId,
        columns: [
          { columnData: nameColumn(row), id: `${assignmentId}-1`, customColor: '#057CB1' },
          { columnData: username, id: `${assignmentId}-2` },
          { columnData: role, id: `${assignmentId}-3` },
          { columnData: study, id: `${assignmentId}-4` }
        ]
      };
    };

    return compose(
      (people) => people.map(getSingleRow),
      (people) => people.sort(SORT_PEOPLE_BY['name']),
      cloneDeep
    )(people);
  };

  const { organizationId, organizationName } = match.params;
  const organizationData = (organizations && organizations.find(({ id }) => String(id) === String(organizationId))) || {
    people: []
  };

  if (loading) {
    paginationApi.hide();
    return <Loading />;
  }

  if (organizationData.people.length === 0) {
    paginationApi.hide();
  }

  return (
    <div id="organization-page">
      <OrganizationDetailsCard organization={organizationData} />
      {organizationData.people.length === 0 && <Notification type="info" data="No assignments found." />}
      {organizationData.people.length > 0 && (
        <Grid
          gridId="organization-details-grid"
          disabled={false}
          bannerLabel={USERS}
          columnDefinition={columnDefinition}
          rows={getRowsData(organizationId, organizationName, peopleData)}
        />
      )}
    </div>
  );
};

OrganizationDetails.propTypes = {
  organizations: PropTypes.array,
  sponsor: PropTypes.object,
  fetchOrganizations: PropTypes.func,
  match: PropTypes.object,
  location: PropTypes.object,
  history: PropTypes.object
};

export default compose(withPagination)(OrganizationDetails);
